/**
 * Platform detection utilities.
 *
 * UA-sniffing is normally a smell, but for the Lens UI we need to distinguish
 * Android (where the system selection toolbar is OS-level and unreachable
 * from web) from iOS (where -webkit-touch-callout: none successfully hides
 * the system callout). There is no CSS or feature-detection alternative —
 * Chromium has explicitly refused to expose a way to suppress the toolbar.
 *
 * On iOS, Safari/Chrome/Edge/Firefox all use WebKit (Apple-mandated), so
 * the UA never contains "Android" and the suppression CSS works uniformly.
 *
 * On Android, Chrome/Samsung Browser/Edge are all Chromium-based with the
 * same toolbar behaviour; Firefox Mobile (Gecko) also surfaces a system
 * toolbar that web can't suppress. The `Android` UA token covers all.
 */

/** True when running on an Android device (any browser). */
export function isAndroid() {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent || '');
}

/** True when running on iOS / iPadOS (any browser, since they're all WebKit). */
export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPad on iOS 13+ reports MacIntel; the touch points check separates it
  // from a real desktop Mac.
  return /iPhone|iPad|iPod/i.test(ua)
    || (ua.includes('Mac') && typeof document !== 'undefined' && navigator.maxTouchPoints > 1);
}

/** True when running specifically on iPhone or iPod — not iPad. */
export function isIPhone() {
  if (typeof navigator === 'undefined') return false;
  // iPhone/iPod always carry their literal token in the UA; iPadOS 13+
  // reports MacIntel and so won't match this check.
  return /iPhone|iPod/i.test(navigator.userAgent || '');
}
